import { useCallback, useContext } from "react";
import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
  useMutation,
} from "@tanstack/react-query";
import {
  getStripeClientSecret,
  StripeOptions,
  getUserBillings,
  GetUserBillingsResult,
  getInvoicePlanPreview,
  InvoicePreview,
  createBillingPortalSession,
} from "./billingApi";
import { AppContext } from "../context/app";

export function useUserBillings(
  userUuid?: string,
  options?: UseQueryOptions<GetUserBillingsResult>,
) {
  return useQuery<GetUserBillingsResult>({
    ...options,
    queryKey: ["user-billings", userUuid],
    queryFn: () => getUserBillings(userUuid!),
    enabled: !!userUuid,
  });
}

export const useUpdateUserBillings = (userUuid?: string) => {
  const queryClient = useQueryClient();
  return useCallback(
    (callback: (data?: GetUserBillingsResult) => GetUserBillingsResult) => {
      const keys = ["user-billings", userUuid];
      const data = queryClient.getQueryData<GetUserBillingsResult>(keys);
      const newData = callback(data);
      queryClient.setQueryData(keys, newData);
    },
    [queryClient, userUuid],
  );
};

export const useGetStripeClientSecret = (
  clientUuid: string,
  userUuid: string,
  options?: UseQueryOptions<StripeOptions>,
) => {
  return useQuery<StripeOptions>(
    ["stripe-secret", clientUuid, userUuid],
    () => getStripeClientSecret(clientUuid, userUuid),
    options,
  );
};

export const useGetInvoicePlanPreviewQuery = (
  {
    planUuid,
    billingUuid,
    discountUuid,
    ignoreSetupFee,
    customSetupFee,
    startDate,
  }: {
    planUuid: string;
    billingUuid: string;
    discountUuid: string;
    ignoreSetupFee: boolean;
    customSetupFee: number;
    startDate: string | undefined;
  },
  options?: UseQueryOptions<InvoicePreview>,
) => {
  const { client } = useContext(AppContext);
  return useQuery<InvoicePreview>(
    [
      "invoice-plan-preview",
      planUuid,
      billingUuid,
      discountUuid,
      customSetupFee,
      startDate,
    ],
    () =>
      getInvoicePlanPreview({
        clientUuid: client.uuid,
        planUuid,
        billingUuid,
        discountUuid,
        ignoreSetupFee,
        customSetupFee,
        startDate,
      }),
    options,
  );
};

export const useCreateBillingPortalSession = () => {
  return useMutation({
    mutationFn: async (returnUrl: string) => {
      const response = await createBillingPortalSession(returnUrl);
      return response;
    },
  });
};
